import React, { useState, useEffect } from 'react';
import Button from '../UI/Button';

interface Volunteer {
  id?: string;
  name: string;
  email: string;
  phone: string;
  joinDate: string;
  status: 'active' | 'inactive' | 'pending';
  skills: string[];
  availability: string[];
  totalHours: number;
  lastActivity: string;
  emergencyContact: string;
  notes: string;
  // Additional fields for comprehensive volunteer management
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  county?: string;
  postalCode?: string;
  dateOfBirth?: string;
  occupation?: string;
  motivation?: string;
  previousExperience?: string;
  preferredActivities?: string[];
  maxHoursPerWeek?: number;
  hasTransport?: boolean;
  canWorkWeekends?: boolean;
  languages?: string[];
  medicalConditions?: string;
  emergencyContactRelation?: string;
}

interface VolunteerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (volunteer: Partial<Volunteer>, hoursToAdd?: { hours: number; activity: string; description?: string; date: string }) => void;
  volunteer?: Volunteer | null;
  mode: 'add' | 'edit';
}

const VolunteerModal: React.FC<VolunteerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  volunteer,
  mode
}) => {
  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    county: '',
    postalCode: '',
    dateOfBirth: '',
    occupation: '',
    joinDate: '',
    status: 'pending' as 'active' | 'inactive' | 'pending',
    skills: [] as string[],
    availability: [] as string[],
    preferredActivities: [] as string[],
    languages: [] as string[],
    totalHours: 0,
    maxHoursPerWeek: 0,
    lastActivity: '',
    emergencyContact: '',
    emergencyContactRelation: '',
    motivation: '',
    previousExperience: '',
    medicalConditions: '',
    hasTransport: false,
    canWorkWeekends: false,
    notes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newSkill, setNewSkill] = useState('');
  const [newLanguage, setNewLanguage] = useState('');

  // Hours addition state
  const [hoursToAdd, setHoursToAdd] = useState({
    hours: '',
    activity: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [addingHours, setAddingHours] = useState(false);

  // Helper function to convert ISO date to yyyy-MM-dd format
  const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    return dateString.split('T')[0]; // Extract just the date part
  };

  // Available options
  const availableDays = ['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'];
  const availableActivities = [
    'Îngrijire animale',
    'Socializare',
    'Transport',
    'Veterinar',
    'Îngrijire medicală',
    'Fotografie',
    'Social media',
    'Fundraising',
    'Construcții',
    'Reparații',
    'Amenajări',
    'Educație',
    'Evenimente',
    'Administrație',
    'Curățenie',
    'Plimbări',
    'Dresaj'
  ];

  const romanianCounties = [
    'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani', 'Brașov',
    'Brăila', 'București', 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj', 'Constanța',
    'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj', 'Harghita', 'Hunedoara',
    'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 'Mehedinți', 'Mureș', 'Neamț', 'Olt',
    'Prahova', 'Satu Mare', 'Sălaj', 'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea',
    'Vaslui', 'Vâlcea', 'Vrancea'
  ];

  useEffect(() => {
    if (volunteer && mode === 'edit') {
      // Split name into first and last name if needed
      const nameParts = volunteer.name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setFormData({
        name: volunteer.name || '',
        firstName: volunteer.firstName || firstName,
        lastName: volunteer.lastName || lastName,
        email: volunteer.email || '',
        phone: volunteer.phone || '',
        address: volunteer.address || '',
        city: volunteer.city || '',
        county: volunteer.county || '',
        postalCode: volunteer.postalCode || '',
        dateOfBirth: formatDateForInput(volunteer.dateOfBirth),
        occupation: volunteer.occupation || '',
        joinDate: formatDateForInput(volunteer.joinDate),
        status: volunteer.status || 'pending',
        skills: volunteer.skills || [],
        availability: volunteer.availability || [],
        preferredActivities: volunteer.preferredActivities || [],
        languages: volunteer.languages || [],
        totalHours: volunteer.totalHours || 0,
        maxHoursPerWeek: volunteer.maxHoursPerWeek || 0,
        lastActivity: volunteer.lastActivity || '',
        emergencyContact: volunteer.emergencyContact || '',
        emergencyContactRelation: volunteer.emergencyContactRelation || '',
        motivation: volunteer.motivation || '',
        previousExperience: volunteer.previousExperience || '',
        medicalConditions: volunteer.medicalConditions || '',
        hasTransport: volunteer.hasTransport || false,
        canWorkWeekends: volunteer.canWorkWeekends || false,
        notes: volunteer.notes || ''
      });
    } else if (mode === 'add') {
      setFormData({
        name: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        county: '',
        postalCode: '',
        dateOfBirth: '',
        occupation: '',
        joinDate: new Date().toISOString().split('T')[0],
        status: 'pending',
        skills: [],
        availability: [],
        preferredActivities: [],
        languages: [],
        totalHours: 0,
        maxHoursPerWeek: 0,
        lastActivity: '',
        emergencyContact: '',
        emergencyContactRelation: '',
        motivation: '',
        previousExperience: '',
        medicalConditions: '',
        hasTransport: false,
        canWorkWeekends: false,
        notes: ''
      });
      
      // Reset hours addition state
      setHoursToAdd({
        hours: '',
        activity: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      setAddingHours(false);
    }
  }, [volunteer, mode, isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Update name when firstName or lastName changes
      if (field === 'firstName' || field === 'lastName') {
        newData.name = `${field === 'firstName' ? value : prev.firstName} ${field === 'lastName' ? value : prev.lastName}`.trim();
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleArrayToggle = (field: 'skills' | 'availability' | 'preferredActivities' | 'languages', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const addLanguage = () => {
    if (newLanguage.trim() && !formData.languages.includes(newLanguage.trim())) {
      setFormData(prev => ({
        ...prev,
        languages: [...prev.languages, newLanguage.trim()]
      }));
      setNewLanguage('');
    }
  };

  const removeLanguage = (languageToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter(lang => lang !== languageToRemove)
    }));
  };

  const handleHoursInputChange = (field: string, value: string) => {
    setHoursToAdd(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateHoursForm = () => {
    if (!hoursToAdd.hours || parseFloat(hoursToAdd.hours) <= 0) {
      return 'Numărul de ore trebuie să fie mai mare decât 0';
    }
    if (parseFloat(hoursToAdd.hours) > 24) {
      return 'Numărul de ore nu poate depăși 24 într-o zi';
    }
    if (!hoursToAdd.activity.trim()) {
      return 'Activitatea este obligatorie';
    }
    if (!hoursToAdd.date) {
      return 'Data este obligatorie';
    }
    return null;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Prenumele este obligatoriu';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Numele este obligatoriu';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email-ul este obligatoriu';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email-ul nu este valid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonul este obligatoriu';
    }
    if (!formData.joinDate) {
      newErrors.joinDate = 'Data înregistrării este obligatorie';
    }
    // Emergency contact is now optional - removed validation

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Check if hours are being added
    let hoursData = undefined;
    if (addingHours && mode === 'edit') {
      const hoursError = validateHoursForm();
      if (hoursError) {
        alert(hoursError);
        return;
      }
      
      hoursData = {
        hours: parseFloat(hoursToAdd.hours),
        activity: hoursToAdd.activity,
        description: hoursToAdd.description || undefined,
        date: hoursToAdd.date
      };
    }

    // Use regular phone number as fallback for emergency contact if not provided
    const emergencyContactValue = formData.emergencyContact.trim() || 
      `${formData.firstName} ${formData.lastName} - ${formData.phone}`;

    const volunteerData: Partial<Volunteer> = {
      ...formData,
      id: volunteer?.id || Date.now().toString(),
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      emergencyContact: emergencyContactValue
    };

    onSave(volunteerData, hoursData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === 'add' ? 'Adaugă voluntar nou' : 'Editează voluntar'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informații personale</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prenume *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nume *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data nașterii
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ocupația
                  </label>
                  <input
                    type="text"
                    value={formData.occupation}
                    onChange={(e) => handleInputChange('occupation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Adresa</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresa
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Orașul
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Județul
                  </label>
                  <select
                    value={formData.county}
                    onChange={(e) => handleInputChange('county', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Selectează județul</option>
                    {romanianCounties.map(county => (
                      <option key={county} value={county}>{county}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cod poștal
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* Volunteer Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informații voluntariat</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data înregistrării *
                  </label>
                  <input
                    type="date"
                    value={formData.joinDate}
                    onChange={(e) => handleInputChange('joinDate', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.joinDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.joinDate && <p className="text-red-500 text-xs mt-1">{errors.joinDate}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="pending">În așteptare</option>
                    <option value="active">Activ</option>
                    <option value="inactive">Inactiv</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ore maxime pe săptămână
                  </label>
                  <input
                    type="number"
                    value={formData.maxHoursPerWeek}
                    onChange={(e) => handleInputChange('maxHoursPerWeek', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasTransport"
                    checked={formData.hasTransport}
                    onChange={(e) => handleInputChange('hasTransport', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="hasTransport" className="ml-2 block text-sm text-gray-900">
                    Are mijloc de transport
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="canWorkWeekends"
                    checked={formData.canWorkWeekends}
                    onChange={(e) => handleInputChange('canWorkWeekends', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="canWorkWeekends" className="ml-2 block text-sm text-gray-900">
                    Disponibil în weekend
                  </label>
                </div>
              </div>
            </div>

            {/* Availability */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Disponibilitate</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {availableDays.map(day => (
                  <div key={day} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`day-${day}`}
                      checked={formData.availability.includes(day)}
                      onChange={() => handleArrayToggle('availability', day)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor={`day-${day}`} className="ml-2 block text-sm text-gray-900">
                      {day}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Preferred Activities */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Activități preferate</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {availableActivities.map(activity => (
                  <div key={activity} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`activity-${activity}`}
                      checked={formData.preferredActivities.includes(activity)}
                      onChange={() => handleArrayToggle('preferredActivities', activity)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor={`activity-${activity}`} className="ml-2 block text-sm text-gray-900">
                      {activity}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Skills */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Competențe</h3>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder="Adaugă o competență..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                />
                <Button type="button" variant="outline" onClick={addSkill}>
                  Adaugă
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-dark"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      className="ml-2 text-primary hover:text-primary-dark"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Languages */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Limbi străine</h3>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  placeholder="Adaugă o limbă..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
                />
                <Button type="button" variant="outline" onClick={addLanguage}>
                  Adaugă
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.languages.map((language, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                  >
                    {language}
                    <button
                      type="button"
                      onClick={() => removeLanguage(language)}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Emergency Contact */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Contact de urgență</h3>
              <div className="mb-3">
                <p className="text-sm text-gray-600">
                  Dacă nu completezi contactul de urgență, se va folosi telefonul tău personal ca alternativă.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nume și telefon contact
                  </label>
                  <input
                    type="text"
                    value={formData.emergencyContact}
                    onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                    placeholder="ex: Maria Popescu - 0721234567 (opțional)"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.emergencyContact ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.emergencyContact && <p className="text-red-500 text-xs mt-1">{errors.emergencyContact}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relația cu contactul
                  </label>
                  <input
                    type="text"
                    value={formData.emergencyContactRelation}
                    onChange={(e) => handleInputChange('emergencyContactRelation', e.target.value)}
                    placeholder="ex: Soț/Soție, Părinte, Prieten"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informații suplimentare</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivația pentru voluntariat
                  </label>
                  <textarea
                    value={formData.motivation}
                    onChange={(e) => handleInputChange('motivation', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="De ce vrei să devii voluntar?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Experiența anterioară cu animale
                  </label>
                  <textarea
                    value={formData.previousExperience}
                    onChange={(e) => handleInputChange('previousExperience', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Descrie experiența ta cu animalele..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Condiții medicale relevante
                  </label>
                  <textarea
                    value={formData.medicalConditions}
                    onChange={(e) => handleInputChange('medicalConditions', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Alergii, limitări fizice, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notițe
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Informații suplimentare..."
                  />
                </div>
              </div>
            </div>

            {/* Add Hours Section - Only for Edit Mode */}
            {mode === 'edit' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Adaugă ore voluntariat</h3>
                  <button
                    type="button"
                    onClick={() => setAddingHours(!addingHours)}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      addingHours 
                        ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {addingHours ? 'Anulează adăugarea' : '+ Adaugă ore'}
                  </button>
                </div>

                {addingHours && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Numărul de ore *
                        </label>
                        <input
                          type="number"
                          min="0.5"
                          step="0.5"
                          max="24"
                          value={hoursToAdd.hours}
                          onChange={(e) => handleHoursInputChange('hours', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Ex: 2.5"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Data *
                        </label>
                        <input
                          type="date"
                          value={hoursToAdd.date}
                          onChange={(e) => handleHoursInputChange('date', e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Activitate *
                      </label>
                      <select
                        value={hoursToAdd.activity}
                        onChange={(e) => handleHoursInputChange('activity', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Selectează activitatea</option>
                        {availableActivities.map((activity) => (
                          <option key={activity} value={activity}>
                            {activity}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descriere (opțional)
                      </label>
                      <textarea
                        value={hoursToAdd.description}
                        onChange={(e) => handleHoursInputChange('description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        rows={2}
                        placeholder="Descriere detaliată a activității efectuate..."
                      />
                    </div>

                    <div className="bg-primary-100 p-3 rounded">
                      <p className="text-sm text-primary-dark">
                        <strong>Ore actuale:</strong> {formData.totalHours} ore total
                        {hoursToAdd.hours && ` → ${(formData.totalHours || 0) + parseFloat(hoursToAdd.hours)} ore după adăugare`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button variant="outline" onClick={onClose}>
                Anulează
              </Button>
              <Button variant="primary" type="submit">
                {mode === 'add' ? 'Adaugă voluntar' : 'Salvează modificările'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VolunteerModal; 